import * as React from 'react';
import { useEffect, useState } from 'react';
import IconButton from '@mui/joy/IconButton';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Button from "@mui/joy/Button";
import Box from "@mui/joy/Box";
import {
    PersonAddAlt1Rounded,
    Close,
    Done,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    Message,
    PhoneCallback,
    TuneRounded
} from "@mui/icons-material";
import { Chip, CircularProgress, Modal, ModalClose, ModalDialog, Select, Tab, TabList, Tabs, Tooltip } from "@mui/joy";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useSearchParams } from "react-router-dom";
import LeadsDrawer from "../modals/lead/LeadsDrawer";
import DebounceInput from "../../domain/DebounceInput";
import Option from "@mui/joy/Option";
import { GetAllFranchises, GetUser } from "../../axios/Axios";
import ApiService from "../../axios/Bookings-Axios";
import {toast, ToastContainer} from "react-toastify";
import { useCall } from "../../lib/call-context";
import { normalizeUkPhone, ukPhoneVariants } from "../../lib/phone";
import ResponsiveTableWrapper, { ResponsiveFilterBar, FilterBarLeft, FilterBarRight } from "../ui/ResponsiveTableWrapper";
import AISentimentIcon, { SentimentData } from "../ui/AISentimentIcon";

// Define keyframes for the highlight animation
const highlightAnimation = `
  @keyframes highlight-row-animation {
    from {
      background-color: rgba(76, 175, 80, 0.3); /* Semi-transparent green */
    }
    to {
      background-color: transparent;
    }
  }

  /* Apply the animation to all direct child td elements of the highlighted row */
  .highlight-row > td {
    animation: highlight-row-animation 2s ease-out;
  }
`;


interface Franchise {
    location: string;
}

interface User {
    id?: number;
    username?: string;
    first_name: string;
    last_name: string;
    location_permissions: string[];
    is_superuser: boolean;
}

interface Row {
    id: string;
    created_at: string;
    readable_services: string;
    total_cost: string;
    franchise_location: string;
    score: number;
    is_referred?: boolean;
    gutter_clean: boolean;
    conservatory_gutter_clean: boolean;
    roof_clean: boolean;
    sent_roof_quote: boolean;
    expanded_row: RowExpanded;
    contact_status: ContactStatus;
    quote_status: 'preparing' | 'ready' | 'failed';
    can_assign_to_self: boolean;
    assignment_is_active?: boolean;
    assigned_to_name?: string | null;
    assigned_to?: number | null;
    assigned_to_username?: string | null;
    speed_to_lead_seconds?: number | null;
    call_attempt_count: number;
}

interface AssignedUserTab {
    id: number;
    username: string;
    lead_count: number;
}

interface ContactStatus {
    status: string;
    contacted_at?: string | null;
}


interface Status {
    id: string;
    created_at: string;
    status: string;
    user: string;
}

interface RowExpanded {
    full_name: string;
    street_address: string;
    postcode: string;
    phone: string;
    notes: string;
    house_type: string;
    id: string;
    email: string;
    status: Status[];
    latest_sentiment: SentimentData | null;
    customer_created_at: string;
}

function formatDate(old_date: string | Date): string {
    let date = new Date(old_date);
    if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long',
            day: 'numeric', hour: 'numeric', minute: 'numeric'
        });
    }
    return 'No Valid Date';
}

/** Short relative time, top-level only: "10 minutes ago", "3 hours ago", "2 days ago". */
function formatTimeAgo(date: string | Date): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
}

function RelativeDateChip({ date }: { date: string | Date }) {
    return (
        <Tooltip title={formatDate(date)}>
            <Chip color="primary" variant="soft" size="sm">
                {formatTimeAgo(date)}
            </Chip>
        </Tooltip>
    );
}

const getContactStatusColor = (status?: string) => {
    switch (status) {
        case "Not Contacted":
            return "danger";
        case "Active Call":
            return "primary";
        case "Attempted Contact":
            return "neutral";
        case "Contacted":
            return "success";
    }
};

const formatSpeedToLeadTime = (seconds?: number | null): string => {
    if (seconds == null) return 'No call';
    const minutes = Math.max(1, Math.round(seconds / 60));
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.round(hours / 24)}d`;
};

const getSpeedToLeadChip = (seconds?: number | null) => {
    if (seconds == null) {
        return {
            badge: 'S2L',
            value: 'No call',
            label: 'WAITING',
            color: 'neutral' as const,
            borderColor: 'neutral.200',
            badgeBg: 'neutral.100',
        };
    }

    const minutes = seconds / 60;
    if (minutes <= 5) {
        return { badge: 'ELITE', value: formatSpeedToLeadTime(seconds), label: 'TO FIRST CALL', color: 'success' as const, borderColor: 'success.300', badgeBg: 'success.200' };
    }
    if (minutes <= 15) {
        return { badge: 'FAST', value: formatSpeedToLeadTime(seconds), label: 'TO FIRST CALL', color: 'primary' as const, borderColor: 'primary.300', badgeBg: 'primary.200' };
    }
    if (minutes <= 30) {
        return { badge: 'OK', value: formatSpeedToLeadTime(seconds), label: 'TO FIRST CALL', color: 'warning' as const, borderColor: 'warning.300', badgeBg: 'warning.200' };
    }
    return { badge: 'SLOW', value: formatSpeedToLeadTime(seconds), label: 'TO FIRST CALL', color: 'danger' as const, borderColor: 'danger.300', badgeBg: 'danger.200' };
};

function SpeedToLeadChip({ seconds }: { seconds?: number | null }) {
    const chip = getSpeedToLeadChip(seconds);
    return (
        <Chip
            size="lg"
            variant="soft"
            color={chip.color}
            sx={{
                borderRadius: '12px',
                border: '1px solid',
                borderColor: chip.borderColor,
                minWidth: 90,
                justifyContent: 'center',
                px: 0.9,
                py: 0.45,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                <Box
                    component="span"
                    sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: chip.badgeBg,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 8,
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                    }}
                >
                    {chip.badge}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1 }}>
                    <Typography level="title-sm" sx={{ fontWeight: 800, lineHeight: 1 }}>{chip.value}</Typography>
                    <Typography level="body-xs" sx={{ fontSize: 8, fontWeight: 700, lineHeight: 1.1 }}>{chip.label}</Typography>
                </Box>
            </Box>
        </Chip>
    );
}

function CallAttemptsChip({ attempts }: { attempts: number }) {
    const label = attempts > 3 ? '3+' : `${attempts}/3`;
    const color = attempts <= 1 ? "danger" : attempts === 2 ? "warning" : "success";

    return (
        <Tooltip title={`${attempts} outbound sales call attempt${attempts === 1 ? '' : 's'}`}>
            <Chip
                size="lg"
                variant="soft"
                color={color}
                sx={{
                    minWidth: 0,
                    justifyContent: 'flex-start',
                    fontSize: 13,
                    px: 0.8,
                }}
            >
                {label}
            </Chip>
        </Tooltip>
    );
}

function Row(props: {
    row: Row;
    initialOpen?: boolean;
    onRowClick: (rowId: number) => void;
    onAssignAction: (row: Row) => void;
    isHighlighted: boolean;
    showAssignAction: boolean;
    showAISentiment: boolean;
    showSpeedToLead: boolean;
    showFranchiseLocation: boolean;
    isAssigning: boolean;
    currentUserId?: number;
    currentUsername?: string;
}) {
    const { row, isHighlighted, showAISentiment } = props;
    const [open, setOpen] = useState(props.initialOpen || false);
    const { makeCall } = useCall();

    const handleClick = () => props.onRowClick(Number(row.id));

    const handleCallCustomer = async () => {
        if (row.expanded_row.phone) {
            makeCall(row.expanded_row.phone, row.expanded_row.full_name);
        } else {
            toast.error("Customer phone number not available", { containerId: "leads-table" });
        }
    };

    const assignedToMeById =
        row.assigned_to != null &&
        props.currentUserId != null &&
        Number(row.assigned_to) === Number(props.currentUserId);
    const assignedToMeByUsername =
        Boolean(row.assigned_to_username) &&
        Boolean(props.currentUsername) &&
        String(row.assigned_to_username).toLowerCase() === String(props.currentUsername).toLowerCase();
    const isAssignedToMe = Boolean(row.assignment_is_active && (assignedToMeById || assignedToMeByUsername));
    const canClickAssignAction = row.can_assign_to_self || isAssignedToMe;

    const handleAssignToSelf = () => {
        if (!canClickAssignAction) return;
        props.onAssignAction(row);
    };

    const getAssignTooltip = () => {
        if (isAssignedToMe) {
            return 'Assigned to you. Click to unassign';
        }
        if (row.can_assign_to_self) {
            return 'Assign this lead to yourself';
        }
        if (row.assignment_is_active) {
            if (row.assigned_to_name) {
                return `Already assigned to ${row.assigned_to_name}`;
            }
            return 'Already assigned to another salesperson';
        }
        return 'You need an answered inbound/outbound call over 30 seconds with this lead first';
    };


    return (
        <React.Fragment>
            <tr className={isHighlighted ? 'highlight-row' : undefined}>
                <td><IconButton aria-label="expand row" variant="plain" color="neutral" size="sm" onClick={() => setOpen(!open)}>{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></td>
                <td style={{ width: 90, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                        {showAISentiment && <AISentimentIcon sentiment={row.expanded_row.latest_sentiment} />}
                        {props.showAssignAction && (
                            <Tooltip title={getAssignTooltip()} placement="right">
                                <span>
                                    <IconButton
                                        size="sm"
                                        variant={isAssignedToMe ? "solid" : "outlined"}
                                        color={isAssignedToMe ? "warning" : (row.can_assign_to_self && !props.isAssigning ? "success" : "neutral")}
                                        disabled={!canClickAssignAction || props.isAssigning}
                                        onClick={handleAssignToSelf}
                                        sx={{
                                            ml: '10px',
                                            borderRadius: '50%',
                                            width: 30,
                                            height: 30,
                                            minHeight: 30,
                                            minWidth: 30,
                                            borderWidth: '2px',
                                            boxShadow: isAssignedToMe
                                                ? '0 2px 10px rgba(237, 108, 2, 0.35)'
                                                : row.can_assign_to_self
                                                    ? '0 2px 10px rgba(46, 125, 50, 0.35)'
                                                    : '0 1px 5px rgba(0,0,0,0.2)',
                                            bgcolor: isAssignedToMe ? 'warning.500' : (row.can_assign_to_self ? 'success.softBg' : 'neutral.softBg'),
                                            color: isAssignedToMe ? '#fff' : undefined,
                                            transition: 'transform 0.16s ease, box-shadow 0.16s ease',
                                            '&:hover': canClickAssignAction
                                                ? {
                                                    transform: 'translateY(-1px) scale(1.04)',
                                                    boxShadow: isAssignedToMe
                                                        ? '0 6px 14px rgba(237, 108, 2, 0.45)'
                                                        : '0 6px 14px rgba(46, 125, 50, 0.45)',
                                                }
                                                : undefined,
                                            '&.Mui-disabled': {
                                                color: 'neutral.500',
                                                borderColor: 'neutral.300',
                                                bgcolor: 'neutral.softBg',
                                                boxShadow: 'none',
                                                opacity: 1,
                                            },
                                        }}
                                    >
                                        <PersonAddAlt1Rounded fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                </td>
                <td><RelativeDateChip date={row.created_at} /></td>
                <td style={{ width: '75px'}}>
                    {row.is_referred ? (
                        <Chip
                            size="sm"
                            variant="solid"
                            sx={{
                                bgcolor: '#2563eb',
                                color: '#fff',
                                fontWeight: 800,
                                fontSize: '0.68rem',
                                letterSpacing: '0.03em',
                                px: 0.8,
                            }}
                        >
                            REFERRAL
                        </Chip>
                    ) : (
                        <Chip
                            size="lg"
                            color={
                                row.score <= 1
                                    ? "danger"
                                    : row.score === 2
                                        ? "neutral"
                                        : row.score === 3
                                            ? "primary"
                                            : "success"
                            }
                            variant="solid"
                        >
                            {row.score}
                        </Chip>
                    )}
                </td>
                <td style={{ textAlign: 'left', verticalAlign: 'middle', width: 82 }}>
                    <CallAttemptsChip attempts={row.call_attempt_count} />
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                            {row.contact_status ? (
                                row.contact_status.status === "Active Call" ? (
                                    <Typography level="title-md" color="primary" sx={{ fontWeight: 400 }}>Active Call</Typography>
                                ) : (
                                    (() => {
                                        const isContacted = row.contact_status.status === "Contacted";
                                        const contactedAt = isContacted && (row.contact_status.contacted_at != null && row.contact_status.contacted_at !== '')
                                            ? row.contact_status.contacted_at
                                            : isContacted && row.expanded_row?.status?.length
                                                ? row.expanded_row.status
                                                    .filter((s: Status) => s.status === "Contacted")
                                                    .sort((a: Status, b: Status) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]?.created_at
                                                : null;
                                        const contactLabel = isContacted && contactedAt
                                            ? `Contacted ${formatTimeAgo(contactedAt)}`
                                            : row.contact_status.status;
                                        return (
                                            <Chip
                                                variant="soft"
                                                size="sm"
                                                startDecorator={
                                                    {
                                                        "Contacted": <Done />,
                                                        "Attempted Contact": <PhoneCallback />,
                                                        "Not Contacted": <Close />
                                                    }[row.contact_status.status]
                                                }
                                                color={getContactStatusColor(row.contact_status.status)}
                                            >
                                                {contactLabel}
                                            </Chip>
                                        );
                                    })()
                                )
                            ) : (
                                <Chip color={"success"}>Contacted</Chip>
                            )}
                </td>
                {props.showSpeedToLead && (
                    <td style={{ textAlign: 'center' }}>
                        <SpeedToLeadChip seconds={row.speed_to_lead_seconds} />
                    </td>
                )}
                {props.showFranchiseLocation && <td>{row.franchise_location}</td>}
                <td>{row.expanded_row.full_name}</td>
                <td>{row.readable_services}</td>
                <td style={{ textAlign: 'center' }}>
                    {row.quote_status !== 'ready' ? (
                        row.quote_status === 'preparing' ? (
                            <Chip
                                size="sm"
                                variant="soft"
                                color="warning"
                                startDecorator={<CircularProgress size="sm" thickness={2} sx={{ '--CircularProgress-size': '14px' }} />}
                            >
                                Preparing
                            </Chip>
                        ) : (
                            <Chip size="sm" variant="soft" color="danger">Failed</Chip>
                        )
                    ) : row.roof_clean ? (
                        row.sent_roof_quote ? <Done sx={{ color: 'success.500' }} /> : <Close sx={{ color: 'danger.500' }} />
                    ) : (
                        <Typography level="body-xs" textColor="text.tertiary">-</Typography>
                    )}
                </td>
                <td>£{row.total_cost}</td>
                <td>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, overflow: 'visible' }}>
                        <Button sx={{ textWrap: 'nowrap' }} size="sm" color="primary" onClick={handleClick}>Edit/Convert To booking</Button>
                        <IconButton variant="solid" sx={{ textWrap: 'nowrap', flexShrink: 0 }} size="sm" color="primary" onClick={handleCallCustomer}><PhoneCallback /></IconButton>
                    </Box>
                </td>
            </tr>
            <tr>
                <td style={{ height: 0, padding: 0 }} colSpan={11 + (props.showSpeedToLead ? 1 : 0) + (props.showFranchiseLocation ? 1 : 0)}>
                    {open && (
                        <Sheet variant="soft" sx={{ p: 1, pl: 6, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}>
                            <Typography level="body-lg" component="div">Customer Information</Typography>
                            <Table borderAxis="bothBetween" size="sm" aria-label="customer details">
                                <thead><tr><th>Full Name</th><th>Number</th><th>Email</th><th>Street Address</th><th>Postcode</th></tr></thead>
                                <tbody><tr><td>{row.expanded_row.full_name}</td><td>{row.expanded_row.phone}</td><td>{row.expanded_row.email}</td><td>{row.expanded_row.street_address}</td><td>{row.expanded_row.postcode}</td></tr></tbody>
                            </Table>
                        </Sheet>
                    )}
                </td>
            </tr>
            <tr>
                <td style={{ height: 0, padding: 0 }} colSpan={11 + (props.showSpeedToLead ? 1 : 0) + (props.showFranchiseLocation ? 1 : 0)}>
                    {open && (
                        <Sheet variant="soft" sx={{ p: 1, pl: 6, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}>
                            <Table borderAxis="bothBetween" size="sm" aria-label="notes and status">
                                <thead><tr><th>Notes</th><th>Lead Status History</th></tr></thead>
                                <tbody>
                                <tr>
                                    <td style={{ whiteSpace: 'pre-wrap' }}>{row.expanded_row.notes || 'No notes'}</td>
                                    <td style={{ whiteSpace: 'pre-wrap' }}>
                                        {row.expanded_row.status.map((statusItem) => (
                                            <div key={statusItem.id} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: '5px', marginBottom: '5px' }}>
                                                <Typography level="body-xs"><Chip>{formatDate(statusItem.created_at)}</Chip></Typography>
                                                <Typography level="body-xs">-</Typography>
                                                <Typography level="body-xs">{statusItem.status}</Typography>
                                                <Typography level="body-xs">-</Typography>
                                                <Typography level="body-xs">{statusItem.user}</Typography>
                                            </div>
                                        ))}
                                    </td>
                                </tr>
                                </tbody>
                            </Table>
                        </Sheet>
                    )}
                </td>
            </tr>
        </React.Fragment>
    );
}

const mapRawDataToRow = (data: any[]): Row[] => {
    return data.map(item => {
        const serviceNames = item.services.map((service: any) => service.name.toLowerCase());
        return {
            id: item.id.toString(),
            created_at: item.created_at,
            gutter_clean: serviceNames.includes('gutter clean'),
            conservatory_gutter_clean: serviceNames.includes('conservatory gutter clean'),
            roof_clean: serviceNames.includes('roof clean'),
            sent_roof_quote: item.sent_roof_quote || false,
            contact_status: item.contact_status,
            readable_services: item.readable_services,
            total_cost: item.total_cost,
            franchise_location: item.customer?.franchise_postcode?.franchise?.location || '',
            score: item.score,
            is_referred: Boolean(item.is_referred || item.referral_badge_label || item.referral_discount_percent),
            quote_status: item.quote_status || 'ready',
            can_assign_to_self: Boolean(item.can_assign_to_self),
            assignment_is_active: Boolean(item.assignment_is_active),
            assigned_to_name: item.assigned_to_name || null,
            assigned_to: item.assigned_to ?? null,
            assigned_to_username: item.assigned_to_username || null,
            speed_to_lead_seconds: item.speed_to_lead_seconds ?? null,
            call_attempt_count: item.call_attempt_count ?? 0,
            expanded_row: {
                full_name: item.customer.full_name,
                street_address: item.customer.street_address,
                postcode: item.customer.postcode,
                phone: item.customer.phone,
                notes: item.customer.notes,
                house_type: item.customer.house_type?.name || '',
                id: item.customer.id.toString(),
                email: item.customer.email,
                status: item.status,
                latest_sentiment: item.customer.latest_sentiment || null,
                customer_created_at: item.customer.created_at || '',
            },
        };
    });
};

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number; }) {
    return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

export default function LeadsTable() {
    const [loadedData, setLoadingData] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const [franchises, setFranchises] = useState<Franchise[]>([]);
    const [user, setUser] = useState<User>({ id: undefined, username: '', first_name: '', last_name: '', location_permissions: [], is_superuser: false });
    const [rows, setRows] = useState<Row[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pageCount, setPageCount] = useState(0);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('ready-to-book');
    const [leadTab, setLeadTab] = useState<'all' | 'assigned'>('all');
    const [urlSearchParam, setUrlSearchParam] = useSearchParams();
    const [openLeadId, setOpenLeadId] = useState(0);
    const [openLeadModal, setOpenLeadModal] = useState(false);
    const [highlightedRowId, setHighlightedRowId] = useState<number | null>(null);
    const { makeCall, call, incomingCall } = useCall();
    const [activeCallRow, setActiveCallRow] = useState<Row | null>(null);
    const [assigningLeadId, setAssigningLeadId] = useState<number | null>(null);
    const [assignedUserTabs, setAssignedUserTabs] = useState<AssignedUserTab[]>([]);
    const [selectedAssignedUserId, setSelectedAssignedUserId] = useState<string | null>(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [pendingAssignRow, setPendingAssignRow] = useState<Row | null>(null);
    const [pendingAssignAction, setPendingAssignAction] = useState<'assign' | 'unassign' | null>(null);
    const selectedLocation = localStorage.getItem('location') || '';
    const showFranchiseLocation = selectedLocation.toLowerCase() === 'all';

    const userPermissions = user.location_permissions || [];
    const isSalesOnlyUser = (userPermissions.includes('sales') || userPermissions.includes('sales_admin'))
        && !userPermissions.includes('admin')
        && !userPermissions.includes('super_admin');
    const showSpeedToLead = Boolean(
        user.is_superuser
        || (userPermissions.includes('sales') && !userPermissions.includes('admin') && !userPermissions.includes('super_admin'))
    );
    const tableColumnCount = 11 + (showSpeedToLead ? 1 : 0) + (showFranchiseLocation ? 1 : 0);
    const activeTopTabValue = leadTab === 'all'
        ? 'all'
        : selectedAssignedUserId
            ? `assigned-user-${selectedAssignedUserId}`
            : 'assigned-self';

    useEffect(() => {
        let cancelled = false;
        const remoteNumber = incomingCall?.parameters.From || call?.parameters.To;
        const targetNorm = remoteNumber ? normalizeUkPhone(remoteNumber) : null;

        if (!remoteNumber || !targetNorm) {
            setActiveCallRow(null);
            return () => { cancelled = true; };
        }

        const terms = ukPhoneVariants(remoteNumber);

        (async () => {
            for (const term of terms) {
                try {
                    const resp = await ApiService.getLeads({ search: term, page: 1 });
                    const results = resp?.results || [];
                    if (!results.length) continue;

                    const mapped = mapRawDataToRow(results);
                    const exact = mapped.filter((r) => normalizeUkPhone(r.expanded_row.phone) === targetNorm);
                    if (!exact.length) continue;

                    // Pick latest CUSTOMER (highest customer id) among exact phone matches
                    const best = exact
                        .slice()
                        .sort((a, b) => Number(b.expanded_row.id) - Number(a.expanded_row.id))[0];

                    if (!cancelled) setActiveCallRow(best || null);
                    return;
                } catch {
                    // try next term
                }
            }
            if (!cancelled) setActiveCallRow(null);
        })();

        return () => { cancelled = true; };
    }, [call, incomingCall]);

    const getLabelDisplayedRowsTo = () => Math.min(pageCount, (page + 1) * rowsPerPage);

    const fetchData = async () => {
        setLoadingData(false);
        try {
            const leadsResponse = await ApiService.getLeads({
                page: page + 1,
                search,
                filter,
                leadTab: isSalesOnlyUser ? leadTab : undefined,
                assignedToUserId: isSalesOnlyUser && leadTab === 'assigned' && selectedAssignedUserId
                    ? selectedAssignedUserId
                    : undefined,
            });
            const nextAssignedUserTabs: AssignedUserTab[] = leadsResponse.assigned_users || [];
            setAssignedUserTabs(nextAssignedUserTabs);
            if (selectedAssignedUserId && !nextAssignedUserTabs.some((u) => String(u.id) === String(selectedAssignedUserId))) {
                setSelectedAssignedUserId(null);
            }
            setRows(mapRawDataToRow(leadsResponse.results || []));
            setPageCount(leadsResponse.count || 0);
        } catch (error) {
            console.error("Failed to fetch leads:", error);
            setRows([]);
            setPageCount(0);
        } finally {
            setLoadingData(true);
        }
    };

    useEffect(() => {
        if (!initialDataLoaded) return;
        fetchData();
    }, [page, search, filter, leadTab, selectedAssignedUserId, isSalesOnlyUser, initialDataLoaded]);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [userResponse, franchisesResponse] = await Promise.all([
                    GetUser(),
                    GetAllFranchises()
                ]);
                const permissions = userResponse.data.location_permissions || [];
                setFilter(permissions.includes('sales') ? 'sales' : 'ready-to-book');
                setUser(userResponse.data);
                setFranchises(franchisesResponse.data);
            } catch (error) {
                console.error("Failed to fetch initial data:", error);
            } finally {
                setInitialDataLoaded(true);
            }
        };
        fetchInitialData();
    }, []);

    const handleNewOpenQuoteModal = () => handleOpenLeadModal(-1);
    const handleOpenLeadModal = (rowId: number) => {
        setOpenLeadModal(false);
        setTimeout(() => {
            setOpenLeadModal(true);
            setOpenLeadId(rowId);
        }, 100);
    };

    const handleCallback = (updateInfo?: { id: number }) => {
        setOpenLeadModal(false);
        if (updateInfo && updateInfo.id) {
            // If a specific lead was updated or created, set it for highlighting
            if (updateInfo.id !== -1) {
                setHighlightedRowId(updateInfo.id);
            }
            // Refetch data to show changes or the new lead
            fetchData().then(() => {
                // After data is fetched, start a timer to remove the highlight class
                if (updateInfo.id !== -1) {
                    setTimeout(() => {
                        setHighlightedRowId(null);
                    }, 2000); // Highlight for 2 seconds
                }
            });
        }
    };

    const handleDebounce = (value: string) => {
        setPage(0);
        setSearch(value);
    };
    const handleChangeFilter = (event: React.SyntheticEvent | null, newValue: string | null) => {
        if (newValue) {
            setPage(0);
            setFilter(newValue);
        }
    };

    const handleChangeLeadTab = (_event: React.SyntheticEvent | null, newValue: string | null) => {
        if (newValue === 'all') {
            setLeadTab('all');
            setSelectedAssignedUserId(null);
            setPage(0);
            return;
        }

        if (newValue === 'assigned-self') {
            setLeadTab('assigned');
            setSelectedAssignedUserId(null);
            setPage(0);
            return;
        }

        if (newValue?.startsWith('assigned-user-')) {
            const userId = newValue.replace('assigned-user-', '');
            setLeadTab('assigned');
            setSelectedAssignedUserId(userId || null);
        }
        setPage(0);
    };

    const handleAssignAction = (row: Row) => {
        const currentUserId = user.id;
        const assignedToMeById =
            row.assigned_to != null &&
            currentUserId != null &&
            Number(row.assigned_to) === Number(currentUserId);
        const assignedToMeByUsername =
            Boolean(row.assigned_to_username) &&
            Boolean(user.username) &&
            String(row.assigned_to_username).toLowerCase() === String(user.username).toLowerCase();
        const isAssignedToMe = Boolean(row.assignment_is_active && (assignedToMeById || assignedToMeByUsername));
        const actionType: 'assign' | 'unassign' | null = isAssignedToMe
            ? 'unassign'
            : row.can_assign_to_self
                ? 'assign'
                : null;
        if (!actionType) return;
        setPendingAssignRow(row);
        setPendingAssignAction(actionType);
        setConfirmModalOpen(true);
    };

    const handleConfirmAssignAction = async () => {
        if (!pendingAssignRow || !pendingAssignAction) return;
        const leadId = Number(pendingAssignRow.id);
        setAssigningLeadId(leadId);
        try {
            if (pendingAssignAction === 'assign') {
                await ApiService.assignLeadToSelf(leadId);
                toast.success("Lead assigned to you", { containerId: "leads-table" });
            } else {
                await ApiService.unassignLeadFromSelf(leadId);
                toast.success("Lead unassigned", { containerId: "leads-table" });
            }
            await fetchData();
        } catch (error: any) {
            const message = error?.response?.data?.error || "Unable to update lead assignment right now.";
            toast.error(message, { containerId: "leads-table" });
        } finally {
            setAssigningLeadId(null);
            setConfirmModalOpen(false);
            setPendingAssignRow(null);
            setPendingAssignAction(null);
        }
    };

    return (
        <>
            <style>{highlightAnimation}</style>
            <ToastContainer containerId={"leads-table"} position="top-right" autoClose={5000} theme="dark" />

            {isSalesOnlyUser && (
                <Box sx={{ mb: 1 }}>
                    <Tabs value={activeTopTabValue} onChange={handleChangeLeadTab}>
                        <TabList>
                            <Tab value="all">All Leads</Tab>
                            <Tab value="assigned-self">My Assigned Leads</Tab>
                            {assignedUserTabs.map((tabUser) => (
                                <Tab key={tabUser.id} value={`assigned-user-${tabUser.id}`}>
                                    {tabUser.username}'s Assigned Leads
                                </Tab>
                            ))}
                        </TabList>
                    </Tabs>
                </Box>
            )}

            <ResponsiveFilterBar>
                <FilterBarLeft>
                    <DebounceInput 
                        sx={{ width: { xs: '100%', sm: '250px' } }} 
                        startDecorator={<SearchRoundedIcon />}
                        placeholder="Search by any field..."
                        debounceTimeout={1000}
                        defaultValue={urlSearchParam.get("lead-id") as string}
                        handleDebounce={handleDebounce}
                    />
                    <Select
                        placeholder="Filter by..."
                        value={filter}
                        onChange={handleChangeFilter}
                        startDecorator={<TuneRounded />}
                        renderValue={(selected) => (<Box sx={{ display: 'flex', gap: '0.25rem' }}><Chip variant="soft" color="primary">{selected?.label}</Chip></Box>)}
                        sx={{ minWidth: { xs: '100%', sm: '15rem' } }}
                    >
                        <Option value="all">All Leads</Option>
                        <Option value="sales">Sales View</Option>
                        <Option value="ready-to-book">Latest</Option>
                        <Option value="booked-in">Booked In</Option>
                        <Option value="opt-out">Opted Out</Option>
                    </Select>
                </FilterBarLeft>
                <FilterBarRight>
                    <Button onClick={handleNewOpenQuoteModal}>Create New Lead</Button>
                </FilterBarRight>
            </ResponsiveFilterBar>

            {/* Active Call UI removed per request */}

            <ResponsiveTableWrapper minWidth={1380}>
                <Table
                    aria-label="collapsible table"
                    sx={{
                        mt: 2,
                        tableLayout: 'fixed',
                        '& th, & td': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                    }}
                >
                    <thead>
                    <tr>
                        <th style={{ width: 40 }} aria-label="empty" />
                        <th style={{ width: 70 }} aria-label="AI Insights" title="AI Insights" />
                        <th style={{ width: '105px' }}>Created</th>
                        <th style={{ width: '75px' }}>Intent</th>
                        <th style={{ width: '120px', whiteSpace: 'nowrap' }}>Call Attempts</th>
                        <th style={{ width: '160px' }}>Contact Status</th>
                        {showSpeedToLead && <th style={{ width: '115px' }}>S2L</th>}
                        {showFranchiseLocation && <th style={{ width: '150px' }}>Franchise</th>}
                        <th style={{ width: '180px' }}>Customer Name</th>
                        <th style={{ width: '160px' }}>Selected Services</th>
                        <th style={{ width: '100px' }}>Quote Sent</th>
                        <th style={{ width: '90px' }}>Total Cost</th>
                        <th style={{ width: '280px' }} aria-label="last" />
                    </tr>
                    </thead>
                    <tbody>
                    {loadedData ? (
                        rows.map((row) => (
                            <Row
                                key={row.id}
                                row={row}
                                initialOpen={search.length > 0}
                                onRowClick={handleOpenLeadModal}
                                onAssignAction={handleAssignAction}
                                showAssignAction={isSalesOnlyUser}
                                showAISentiment={(user.location_permissions || []).includes('super_admin')}
                                showSpeedToLead={showSpeedToLead}
                                showFranchiseLocation={showFranchiseLocation}
                                isAssigning={assigningLeadId === Number(row.id)}
                                currentUserId={user.id}
                                currentUsername={user.username}
                                isHighlighted={Number(row.id) === highlightedRowId}
                            />
                        ))
                    ) : (
                        <tr><td colSpan={tableColumnCount} style={{ textAlign: 'center', padding: '2rem' }}><CircularProgress /></td></tr>
                    )}
                    </tbody>
                    <tfoot>
                    <tr>
                        <td colSpan={tableColumnCount}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                                <Typography textAlign="center" sx={{ minWidth: 80 }}>
                                    {labelDisplayedRows({ from: pageCount === 0 ? 0 : page * rowsPerPage + 1, to: getLabelDisplayedRowsTo(), count: pageCount })}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton size="sm" color="neutral" variant="outlined" disabled={page === 0} onClick={() => setPage(page - 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowLeft /></IconButton>
                                    <IconButton size="sm" color="neutral" variant="outlined" disabled={page >= Math.ceil(pageCount / rowsPerPage) - 1} onClick={() => setPage(page + 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowRight /></IconButton>
                                </Box>
                            </Box>
                        </td>
                    </tr>
                    </tfoot>
                </Table>
            </ResponsiveTableWrapper>
            <React.Fragment>
                <LeadsDrawer key={'leads-modal-' + openLeadModal} openState={openLeadModal}
                             leadId={openLeadId} parentCallback={handleCallback}/>
            </React.Fragment>
            <Modal open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)}>
                <ModalDialog size="sm">
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 1 }}>
                        {pendingAssignAction === 'unassign' ? 'Unassign lead?' : 'Assign lead to you?'}
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 2 }}>
                        {pendingAssignAction === 'unassign'
                            ? 'This lead will be removed from your assigned leads.'
                            : 'Confirm you want to assign this lead to yourself.'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => setConfirmModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color={pendingAssignAction === 'unassign' ? 'warning' : 'success'}
                            loading={Boolean(assigningLeadId)}
                            onClick={handleConfirmAssignAction}
                        >
                            Confirm
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </>
    );
}